import { useEffect, useMemo, useRef, useState } from "react";
import "./App.css";

import AccountPanels from "./components/AccountPanels";
import AuthModals from "./components/AuthModals";

import { money } from "./utils/money";
import {
  getSavedUser,
  getSessionId
} from "./utils/session";

import {
  stockMessage
} from "./utils/messages";

function normalizeProductImageUrl(value) {
  if (!value) return "";
  const url = String(value).trim();

  if (!url) return "";

  if (url.startsWith("//")) {
    return `https:${url}`;
  }

  return url;
}

function getProductImageSrc(value) {
  const url = normalizeProductImageUrl(value);

  if (!url) return "";

  if (/^https?:\/\//i.test(url)) {
    return `/api/inventory/admin/tvc/image-proxy?url=${encodeURIComponent(url)}`;
  }

  return url;
}

function handleProductImageError(event) {
  event.currentTarget.style.display = "none";

  const fallback = event.currentTarget.parentElement?.querySelector(".product-image-fallback");

  if (fallback) {
    fallback.style.display = "flex";
  }
}



const API_PRODUCTS_URL = "/api/products/products";
const API_CART_URL = "/api/cart/cart";
const API_CHECKOUT_URL = "/api/inventory/checkout";
const API_ORDERS_URL = "/api/inventory/orders";
const API_LOGIN_URL = "/api/customers/customers/login";
const API_REGISTER_URL = "/api/customers/customers/register";
const API_CUSTOMERS_URL = "/api/customers/customers";

const API_JAVA_URL = "/api/payments";
const API_SEARCH_URL = `${API_JAVA_URL}/search`;
const API_RECOMMENDATIONS_URL = `${API_JAVA_URL}/recommendations`;
const API_CONTACT_URL = `${API_JAVA_URL}/contact`;

const CATALOG_PAGE_SIZE = 12;


function emptyAddressForm(user) {
  return {
    address_type: "shipping",
    address_name: "Casa",
    full_name: user?.full_name || "",
    phone: "",
    street: "",
    exterior_number: "",
    interior_number: "",
    neighborhood: "",
    city: "",
    state: "",
    postal_code: "",
    country: "México",
    address_references: "",
    is_default: true
  };
}

function emptyBillingForm(user) {
  return {
    business_name: "",
    tax_id: "",
    tax_regime: "",
    cfdi_use: "G03",
    email: user?.email || "",
    street: "",
    exterior_number: "",
    interior_number: "",
    neighborhood: "",
    city: "",
    state: "",
    postal_code: "",
    country: "México",
    is_default: true
  };
}

function categoryParts(value) {
  return String(value || "Sin categoría")
    .split(">")
    .map((part) => part.trim())
    .filter(Boolean);
}

function mainCategory(value) {
  const parts = categoryParts(value);
  return parts[0] || "Sin categoría";
}

function shortCategory(value) {
  const parts = categoryParts(value);

  if (parts.length === 0) {
    return "Sin categoría";
  }

  if (parts.length === 1) {
    return parts[0];
  }

  return `${parts[0]} · ${parts[parts.length - 1]}`;
}

function availabilityLabel(value) {
  const available = Number(value || 0);

  if (available <= 0) {
    return "Sin inventario";
  }

  if (available <= 20) {
    return `Disponible: ${available}`;
  }

  return "Disponible";
}


function normalizeStoreProduct(product) {
  if (!product) return product;

  const image = product.image_icon || product.image_url || "";
  const available = Number(
    product.available ?? product.stock ?? (product.is_active === false ? 0 : 1)
  );

  return {
    ...product,
    image_icon: image,
    category:
      product.category ||
      product.category_name ||
      (product.category_id ? `Categoría ${product.category_id}` : "Recomendados"),
    available,
    badge: product.badge || (available > 0 ? "Disponible" : "Sin inventario")
  };
}

export default function App() {
  const [products, setProducts] = useState([]);
  const [cartData, setCartData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [billingProfiles, setBillingProfiles] = useState([]);

  const [category, setCategory] = useState("Todos");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("featured");
  const [currentPage, setCurrentPage] = useState(1);

  const [searchResults, setSearchResults] = useState([]);
  const [searchBusy, setSearchBusy] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [remoteSearchActive, setRemoteSearchActive] = useState(false);

  const [homeRecommendations, setHomeRecommendations] = useState([]);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [ordersOpen, setOrdersOpen] = useState(false);
  const [addressesOpen, setAddressesOpen] = useState(false);
  const [billingOpen, setBillingOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingCart, setLoadingCart] = useState(true);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingAddresses, setLoadingAddresses] = useState(false);
  const [loadingBilling, setLoadingBilling] = useState(false);

  const [errorProducts, setErrorProducts] = useState("");
  const [cartBusy, setCartBusy] = useState(false);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const checkoutLockRef = useRef(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [addressBusy, setAddressBusy] = useState(false);
  const [billingBusy, setBillingBusy] = useState(false);

  const [user, setUser] = useState(getSavedUser());

  const [showLogin, setShowLogin] = useState(false);
  const [showRegister, setShowRegister] = useState(false);

  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState("");

  const [loginForm, setLoginForm] = useState({
    email: "",
    password: ""
  });

  const [registerForm, setRegisterForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    password: ""
  });

  const [addressForm, setAddressForm] = useState(emptyAddressForm(getSavedUser()));
  const [billingForm, setBillingForm] = useState(emptyBillingForm(getSavedUser()));

  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [selectedBillingId, setSelectedBillingId] = useState("");

  const [lastOrder, setLastOrder] = useState(null);

  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [contactMessage, setContactMessage] = useState("");
  const [contactStatus, setContactStatus] = useState("");
  const [contactError, setContactError] = useState("");
  const [contactBusy, setContactBusy] = useState(false);

  async function loadProducts() {
    try {
      setLoadingProducts(true);

      const response = await fetch(API_PRODUCTS_URL);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      setProducts(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      setErrorProducts("No fue posible cargar el catálogo.");
    } finally {
      setLoadingProducts(false);
    }
  }
  async function loadProductRecommendations(productId) {
    if (!productId) {
      await loadHomeRecommendations();
      return;
    }

    try {
      setLoadingRecommendations(true);

      const response = await fetch(
        `${API_RECOMMENDATIONS_URL}/product/${productId}?limit=8`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const items = Array.isArray(data.items)
        ? data.items.map(normalizeStoreProduct)
        : [];

      setHomeRecommendations(items);
    } catch (error) {
      console.error(error);
      await loadHomeRecommendations();
    } finally {
      setLoadingRecommendations(false);
    }
  }




  async function loadHomeRecommendations() {
    try {
      setLoadingRecommendations(true);

      const response = await fetch(`${API_RECOMMENDATIONS_URL}/home?limit=8`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const items = Array.isArray(data.items) ? data.items.map(normalizeStoreProduct) : [];

      setHomeRecommendations(items);
    } catch (error) {
      console.error(error);
      setHomeRecommendations([]);
    } finally {
      setLoadingRecommendations(false);
    }
  }

  async function runRemoteSearch(query) {
    const cleanQuery = String(query || "").trim();

    if (cleanQuery.length < 2) {
      setRemoteSearchActive(false);
      setSearchResults([]);
      setSearchError("");
      return;
    }

    try {
      setSearchBusy(true);
      setSearchError("");
      setRemoteSearchActive(true);

      const response = await fetch(
        `${API_SEARCH_URL}?q=${encodeURIComponent(cleanQuery)}&limit=40`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const items = Array.isArray(data.items) ? data.items.map(normalizeStoreProduct) : [];

      setSearchResults(items);

      if (items.length > 0) {
        await loadProductRecommendations(items[0].id);
      } else {
        setHomeRecommendations([]);
      }
    } catch (error) {
      console.error(error);
      setSearchError("No fue posible realizar la búsqueda.");
      setSearchResults([]);
      setRemoteSearchActive(true);
    } finally {
      setSearchBusy(false);
    }
  }

  async function loadCart(sessionIdOverride) {
    try {
      setLoadingCart(true);

      const sessionId = sessionIdOverride || getSessionId(user);

      const response = await fetch(
        `${API_CART_URL}?session_id=${encodeURIComponent(sessionId)}`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      setCartData(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingCart(false);
    }
  }

  async function loadOrders(sessionIdOverride) {
    try {
      setLoadingOrders(true);

      const sessionId = sessionIdOverride || getSessionId(user);

      const response = await fetch(
        `${API_ORDERS_URL}?session_id=${encodeURIComponent(sessionId)}`
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      setOrders(Array.isArray(data.orders) ? data.orders : []);
    } catch (error) {
      console.error(error);
      alert("No fue posible cargar tus pedidos.");
    } finally {
      setLoadingOrders(false);
    }
  }

  async function loadAddresses(userOverride) {
    const activeUser = userOverride || user;

    if (!activeUser?.id) {
      setAddresses([]);
      setSelectedAddressId("");
      return [];
    }

    try {
      setLoadingAddresses(true);

      const response = await fetch(`${API_CUSTOMERS_URL}/${activeUser.id}/addresses`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const list = Array.isArray(data.addresses) ? data.addresses : [];

      setAddresses(list);

      const defaultAddress = list.find((address) => address.is_default) || list[0];

      if (defaultAddress) {
        setSelectedAddressId(String(defaultAddress.id));
      } else {
        setSelectedAddressId("");
      }

      return list;
    } catch (error) {
      console.error(error);
      alert("No fue posible cargar tus direcciones.");
      return [];
    } finally {
      setLoadingAddresses(false);
    }
  }

  async function loadBillingProfiles(userOverride) {
    const activeUser = userOverride || user;

    if (!activeUser?.id) {
      setBillingProfiles([]);
      setSelectedBillingId("");
      return [];
    }

    try {
      setLoadingBilling(true);

      const response = await fetch(`${API_CUSTOMERS_URL}/${activeUser.id}/billing`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      const list = Array.isArray(data.billing_profiles) ? data.billing_profiles : [];

      setBillingProfiles(list);

      const defaultBilling = list.find((profile) => profile.is_default) || list[0];

      if (defaultBilling) {
        setSelectedBillingId(String(defaultBilling.id));
      } else {
        setSelectedBillingId("");
      }

      return list;
    } catch (error) {
      console.error(error);
      alert("No fue posible cargar tus datos fiscales.");
      return [];
    } finally {
      setLoadingBilling(false);
    }
  }

  useEffect(() => {
    const savedUser = getSavedUser();

    loadProducts();
    loadCart(getSessionId(savedUser));

    if (savedUser?.id) {
      loadAddresses(savedUser);
      loadBillingProfiles(savedUser);
    }
  }, []);


  useEffect(() => {
    loadHomeRecommendations();
  }, []);

  useEffect(() => {
    const cleanQuery = search.trim();

    if (cleanQuery.length < 2) {
      setRemoteSearchActive(false);
      setSearchResults([]);
      setSearchError("");
      return;
    }

    const timer = setTimeout(() => {
      runRemoteSearch(cleanQuery);
    }, 350);

  return () => clearTimeout(timer);
  }, [search]);

  const cartItems = cartData?.items || [];

  const totals = cartData?.totals || {
    subtotal: 0,
    shipping: 0,
    total: 0
  };

  const totalItems = cartItems.reduce(
    (sum, item) => sum + Number(item.quantity),
    0
  );

  const categories = useMemo(() => {
    const unique = [
      ...new Set(
        products
          .map((product) => mainCategory(product.category))
          .filter(Boolean)
      )
    ];

    return ["Todos", ...unique.sort()];
  }, [products]);

  const catalogProducts = remoteSearchActive ? searchResults : products;

  const filteredProducts = useMemo(() => {
    let list = catalogProducts.filter((product) => {
      const byCategory =
        remoteSearchActive ||
        category === "Todos" ||
        mainCategory(product.category) === category;

      const text =
        `${product.name || ""} ${product.brand || ""} ${product.category || ""} ${product.sku || ""}`.toLowerCase();

      const bySearch =
        remoteSearchActive ||
        text.includes(search.toLowerCase());

      return byCategory && bySearch;
    });

    if (sort === "price-low") {
      list = [...list].sort(
        (a, b) => Number(a.price) - Number(b.price)
      );
    }

    if (sort === "price-high") {
      list = [...list].sort(
        (a, b) => Number(b.price) - Number(a.price)
      );
    }

    if (sort === "rating") {
      list = [...list].sort(
        (a, b) => Number(b.rating) - Number(a.rating)
      );
    }

    return list;
  }, [catalogProducts, category, search, sort, remoteSearchActive]);

  const totalCatalogProducts = filteredProducts.length;
  const totalCatalogPages = Math.max(
    1,
    Math.ceil(totalCatalogProducts / CATALOG_PAGE_SIZE)
  );

  const safeCurrentPage = Math.min(currentPage, totalCatalogPages);
  const catalogStartIndex = (safeCurrentPage - 1) * CATALOG_PAGE_SIZE;
  const catalogEndIndex = Math.min(
    catalogStartIndex + CATALOG_PAGE_SIZE,
    totalCatalogProducts
  );

  const paginatedProducts = filteredProducts.slice(
    catalogStartIndex,
    catalogEndIndex
  );

  const paginationPages = useMemo(() => {
    const pages = [];
    const visiblePages = 5;

    let start = Math.max(1, safeCurrentPage - 2);
    let end = Math.min(totalCatalogPages, start + visiblePages - 1);

    start = Math.max(1, end - visiblePages + 1);

    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }

    return pages;
  }, [safeCurrentPage, totalCatalogPages]);

  useEffect(() => {
    setCurrentPage(1);
  }, [category, search, sort, remoteSearchActive]);

  useEffect(() => {
    if (currentPage > totalCatalogPages) {
      setCurrentPage(totalCatalogPages);
    }
  }, [currentPage, totalCatalogPages]);

  function renderCatalogPagination(position = "top") {
    if (loadingProducts || searchBusy || totalCatalogProducts <= CATALOG_PAGE_SIZE) {
      return null;
    }

    return (
      <div className={`catalog-pagination catalog-pagination-${position}`}>
        <div className="catalog-pagination-summary">
          Mostrando {catalogStartIndex + 1}-{catalogEndIndex} de {totalCatalogProducts} productos
        </div>

        <div className="catalog-pagination-controls">
          <button
            type="button"
            disabled={safeCurrentPage <= 1}
            onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
          >
            Anterior
          </button>

          {paginationPages.map((page) => (
            <button
              type="button"
              key={`catalog-page-${position}-${page}`}
              className={page === safeCurrentPage ? "active" : ""}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </button>
          ))}

          <button
            type="button"
            disabled={safeCurrentPage >= totalCatalogPages}
            onClick={() => setCurrentPage((page) => Math.min(totalCatalogPages, page + 1))}
          >
            Siguiente
          </button>
        </div>
      </div>
    );
  }


  function saveUser(userData) {
    localStorage.setItem("intectec_user", JSON.stringify(userData));

    setUser(userData);
    setAddressForm(emptyAddressForm(userData));
    setBillingForm(emptyBillingForm(userData));

    setShowLogin(false);
    setShowRegister(false);

    setAuthError("");
  }

  async function logout() {
    localStorage.removeItem("intectec_user");

    setUser(null);
    setLastOrder(null);
    setOrders([]);
    setAddresses([]);
    setBillingProfiles([]);
    setSelectedAddressId("");
    setSelectedBillingId("");
    setOrdersOpen(false);
    setAddressesOpen(false);
    setBillingOpen(false);

    await loadCart("demo-session");
  }

  async function loginSubmit(event) {
    event.preventDefault();

    try {
      setAuthBusy(true);
      setAuthError("");

      const response = await fetch(API_LOGIN_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(loginForm)
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data.error ||
            data.message ||
            "Credenciales inválidas"
        );
      }

      const loggedUser = data.customer
        ? {
            id: data.customer.id,
            full_name: data.customer.full_name,
            email: data.customer.email,
            session_id: data.session_id || `customer-${data.customer.id}`
          }
        : {
            id: data.id,
            full_name: data.full_name,
            email: data.email,
            session_id: data.session_id || `customer-${data.id}`
          };

      if (!loggedUser.id || !loggedUser.email) {
        throw new Error("Respuesta de login inválida");
      }

      saveUser(loggedUser);

      await loadCart(getSessionId(loggedUser));
      await loadAddresses(loggedUser);
      await loadBillingProfiles(loggedUser);
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setAuthBusy(false);
    }
  }

  async function registerSubmit(event) {
    event.preventDefault();

    try {
      setAuthBusy(true);
      setAuthError("");

      const response = await fetch(API_REGISTER_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(registerForm)
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data.error ||
            data.detail ||
            data.message ||
            "No fue posible registrar"
        );
      }

      setShowRegister(false);
      setShowLogin(true);

      setLoginForm({
        email: registerForm.email,
        password: ""
      });

      setRegisterForm({
        full_name: "",
        email: "",
        phone: "",
        password: ""
      });
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setAuthBusy(false);
    }
  }

  async function addToCart(product) {
    try {
      setCartBusy(true);
      setLastOrder(null);

      const response = await fetch(`${API_CART_URL}/items`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          session_id: getSessionId(user),
          product_id: product.id,
          quantity: 1
        })
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (
          response.status === 409 &&
          data.reason === "insufficient stock"
        ) {
          alert(
            stockMessage(
              product.name,
              data.available,
              data.requested
            )
          );

          await loadProducts();
          await loadCart(getSessionId(user));
          return;
        }

        throw new Error(
          data.error ||
            data.detail ||
            "No fue posible agregar el producto"
        );
      }

      setCartData(data);
      setDrawerOpen(true);
    } catch (error) {
      console.error(error);
      alert(error.message || "Error agregando producto");
    } finally {
      setCartBusy(false);
    }
  }

  async function updateQuantity(cartItemId, quantity) {
    if (quantity <= 0) {
      await removeItem(cartItemId);
      return;
    }

    try {
      setCartBusy(true);

      const item = cartItems.find(
        (cartItem) => cartItem.cart_item_id === cartItemId
      );

      if (item && quantity > Number(item.available)) {
        alert(
          stockMessage(
            item.name,
            item.available,
            quantity
          )
        );

        return;
      }

      const response = await fetch(`${API_CART_URL}/items/${cartItemId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          session_id: getSessionId(user),
          quantity
        })
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (
          response.status === 409 &&
          data.reason === "insufficient stock"
        ) {
          alert(
            stockMessage(
              item?.name,
              data.available,
              data.requested || quantity
            )
          );

          await loadProducts();
          await loadCart(getSessionId(user));
          return;
        }

        throw new Error(
          data.error ||
            data.detail ||
            "Error actualizando carrito"
        );
      }

      setCartData(data);
    } catch (error) {
      console.error(error);
      alert(error.message || "Error actualizando carrito");
    } finally {
      setCartBusy(false);
    }
  }

  async function removeItem(cartItemId) {
    try {
      setCartBusy(true);

      const response = await fetch(
        `${API_CART_URL}/items/${cartItemId}?session_id=${encodeURIComponent(
          getSessionId(user)
        )}`,
        {
          method: "DELETE"
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data.error ||
            data.detail ||
            "Error eliminando producto"
        );
      }

      setCartData(data);
    } catch (error) {
      console.error(error);
      alert(error.message || "Error eliminando producto");
    } finally {
      setCartBusy(false);
    }
  }

  async function clearCart() {
    try {
      setCartBusy(true);

      const response = await fetch(
        `${API_CART_URL}/clear?session_id=${encodeURIComponent(
          getSessionId(user)
        )}`,
        {
          method: "DELETE"
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data.error ||
            data.detail ||
            "Error limpiando carrito"
        );
      }

      setCartData(data);
    } catch (error) {
      console.error(error);
      alert(error.message || "Error limpiando carrito");
    } finally {
      setCartBusy(false);
    }
  }

  async function checkout(event) {
    if (event?.preventDefault) {
      event.preventDefault();
    }

    if (checkoutLockRef.current || checkoutBusy) {
      return;
    }

    if (!user) {
      setShowLogin(true);
      return;
    }

    if (addresses.length === 0) {
      alert("Antes de finalizar tu compra agrega una dirección de envío.");
      await openAddresses();
      return;
    }

    if (!selectedAddressId) {
      alert("Selecciona una dirección de envío para continuar.");
      return;
    }

    try {
      checkoutLockRef.current = true;
      setCheckoutBusy(true);

      const response = await fetch(API_CHECKOUT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          session_id: getSessionId(user),
          customer_email: user.email,
          payment_method: "simulated_card",
          shipping_address_id: Number(selectedAddressId),
          billing_profile_id: selectedBillingId ? Number(selectedBillingId) : null
        })
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        if (
          response.status === 409 &&
          data.error === "insufficient stock"
        ) {
          alert(
            stockMessage(
              `Producto ID ${data.product_id}`,
              data.available,
              data.requested
            )
          );

          await loadProducts();
          await loadCart(getSessionId(user));
          return;
        }

        throw new Error(
          data.error ||
            data.detail ||
            "No fue posible crear la orden"
        );
      }

      setLastOrder(data.order);

      setCartData({
        service: "nodejs-api",
        cart: {
          session_id: getSessionId(user),
          status: "active"
        },
        items: [],
        totals: {
          subtotal: 0,
          shipping: 0,
          total: 0
        }
      });

      await loadProducts();
      await loadOrders(getSessionId(user));
      await loadCart(getSessionId(user));

      alert(`Orden generada correctamente\n\n${data.order.order_number}`);
    } catch (error) {
      console.error(error);
      alert(`Error en checkout: ${error.message}`);
    } finally {
      checkoutLockRef.current = false;
      setCheckoutBusy(false);
    }
  }

  async function openOrders() {
    if (!user) {
      setShowLogin(true);
      return;
    }

    await loadOrders(getSessionId(user));
    setOrdersOpen(true);
  }

  async function openAddresses() {
    if (!user) {
      setShowLogin(true);
      return;
    }

    await loadAddresses();
    setAddressesOpen(true);
  }

  async function openBilling() {
    if (!user) {
      setShowLogin(true);
      return;
    }

    await loadBillingProfiles();
    setBillingOpen(true);
  }

  async function saveAddress(event) {
    event.preventDefault();

    if (!user) {
      setShowLogin(true);
      return;
    }

    if (!addressForm.full_name || !addressForm.street || !addressForm.city || !addressForm.state || !addressForm.postal_code) {
      alert("Completa nombre, calle, ciudad, estado y código postal.");
      return;
    }

    try {
      setAddressBusy(true);

      const response = await fetch(`${API_CUSTOMERS_URL}/${user.id}/addresses`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(addressForm)
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || data.detail || "No fue posible guardar la dirección");
      }

      alert("Dirección guardada correctamente.");
      setAddressForm(emptyAddressForm(user));
      await loadAddresses();
    } catch (error) {
      console.error(error);
      alert(`Error guardando dirección: ${error.message}`);
    } finally {
      setAddressBusy(false);
    }
  }

  async function saveBilling(event) {
    event.preventDefault();

    if (!user) {
      setShowLogin(true);
      return;
    }

    if (!billingForm.business_name || !billingForm.tax_id) {
      alert("Completa razón social y RFC.");
      return;
    }

    try {
      setBillingBusy(true);

      const response = await fetch(`${API_CUSTOMERS_URL}/${user.id}/billing`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(billingForm)
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || data.detail || "No fue posible guardar datos fiscales");
      }

      alert("Datos fiscales guardados correctamente.");
      setBillingForm(emptyBillingForm(user));
      await loadBillingProfiles();
    } catch (error) {
      console.error(error);
      alert(`Error guardando datos fiscales: ${error.message}`);
    } finally {
      setBillingBusy(false);
    }
  }

  async function cancelOrder(order) {
    if (!user) {
      setShowLogin(true);
      return;
    }

    if (order.status !== "created") {
      alert("Este pedido ya no puede ser cancelado.");
      return;
    }

    const confirmCancel = window.confirm(
      `¿Deseas cancelar el pedido ${order.order_number}?\n\n` +
      `Esta acción regresará el inventario reservado y cambiará el estado del pedido a cancelado.`
    );

    if (!confirmCancel) {
      return;
    }

    try {
      setCancelBusy(true);

      const response = await fetch(
        `${API_ORDERS_URL}/${order.id}/cancel`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            session_id: getSessionId(user)
          })
        }
      );

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data.error ||
            data.detail ||
            "No fue posible cancelar el pedido"
        );
      }

      alert(`Pedido cancelado correctamente\n\n${order.order_number}`);

      await loadOrders(getSessionId(user));
      await loadProducts();
      await loadCart(getSessionId(user));
    } catch (error) {
      console.error(error);
      alert(`Error cancelando pedido: ${error.message}`);
    } finally {
      setCancelBusy(false);
    }
  }

  async function handleContactSubmit(event) {
    event.preventDefault();

    const name = contactName.trim();
    const email = contactEmail.trim();
    const phone = contactPhone.trim();
    const message = contactMessage.trim();

    setContactStatus("");
    setContactError("");

    if (!name) {
      setContactError("Ingresa tu nombre o empresa.");
      return;
    }

    if (!email) {
      setContactError("Ingresa tu correo para poder contactarte.");
      return;
    }

    const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    if (!validEmail) {
      setContactError("Ingresa un correo válido.");
      return;
    }

    try {
      setContactBusy(true);

      const response = await fetch(API_CONTACT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          email,
          name,
          phone,
          message: message || "Solicito información sobre productos y soluciones de Intectec Store."
        })
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.message || "No fue posible enviar la solicitud.");
      }

      setContactStatus("Gracias. Tu solicitud fue enviada correctamente.");
      setContactName("");
      setContactEmail("");
      setContactPhone("");
      setContactMessage("");
    } catch (error) {
      console.error(error);
      setContactError(error.message || "No fue posible enviar la solicitud.");
    } finally {
      setContactBusy(false);
    }
  }

  return (
    <>
      <header className="topbar">
        <div className="logo logo-intectec">
          <img src="/grupo-intectec-logo.png" alt="Grupo Intectec" className="brand-logo" />
        </div>

        <nav className="desktop-nav">
          <a href="#catalogo">Catálogo</a>
          <a href="#ofertas">Ofertas</a>
          <a href="#beneficios">Beneficios</a>
          <a href="#contacto">Contacto</a>
        </nav>

        <div className="top-actions">
          {user ? (
            <>
              <button
                className="login-button"
                onClick={openOrders}
              >
                Mis pedidos
              </button>

              <button
                className="login-button"
                onClick={openAddresses}
              >
                Mis direcciones
              </button>

              <button
                className="login-button"
                onClick={openBilling}
              >
                Datos fiscales
              </button>

              <div className="user-pill">
                👤 {user.full_name || user.email}
              </div>

              <button
                className="logout-button"
                onClick={logout}
              >
                Salir
              </button>
            </>
          ) : (
            <>
              <button
                className="login-button"
                onClick={() => setShowLogin(true)}
              >
                Login
              </button>

              <button
                className="register-button"
                onClick={() => setShowRegister(true)}
              >
                Registro
              </button>
            </>
          )}

          <button
            className="cart-button"
            onClick={() => setDrawerOpen(true)}
          >
            🛒
            <span>{totalItems}</span>
          </button>
        </div>
      </header>

      <main className="store">
        <section className="hero">
          <div className="hero-content">
            <span className="eyebrow">Tienda online</span>

            <h1>Soluciones tecnológicas para tu empresa y hogar</h1>

            <p>
              Encuentra productos de seguridad, redes, energía y tecnología con atención personalizada.
            </p>
          </div>
        </section>

        <section className="benefits" id="beneficios">
          <article>
            <span>🚚</span>
            <strong>Disponibilidad al día</strong>
            <p>Consulta productos disponibles y solicita los equipos que necesitas.</p>
          </article>

          <article>
            <span>🔒</span>
            <strong>Compra fácil y segura</strong>
            <p>Agrega productos al carrito y completa tu pedido en pocos pasos.</p>
          </article>

          <article>
            <span>📦</span>
            <strong>Seguimiento de pedidos</strong>
            <p>Consulta el estado de tus compras y administra tus pedidos fácilmente.</p>
          </article>

          <article>
            <span>⭐</span>
            <strong>Catálogo especializado</strong>
            <p>Productos seleccionados para seguridad, conectividad, energía y tecnología.</p>
          </article>
        </section>

        <section className="promo-strip" id="ofertas">
          <div>
            <span>Ofertas Intectec Store</span>
            <strong>Promociones especiales en tecnología, seguridad y redes.</strong>
          </div>

          <button onClick={() => setDrawerOpen(true)}>
            Ver carrito
          </button>
        </section>

        {(loadingRecommendations || homeRecommendations.length > 0) && (
          <section className="recommendations-section" id="recomendados">
            <div className="section-title">
              <div>
                <span className="eyebrow">Recomendaciones</span>
                <h2>{remoteSearchActive ? "Recomendaciones según tu búsqueda" : "Productos recomendados para ti"}</h2>
              </div>

              <p>
                {loadingRecommendations
                  ? "Cargando..."
                  : `${homeRecommendations.length} sugerencias`}
              </p>
            </div>

            {!loadingRecommendations && (
              <div className="recommendation-row">
                {homeRecommendations.map((product) => (
                  <article
                    className="recommendation-card"
                    key={`recommended-${product.id}`}
                  >
                    <button
                      className="recommendation-image"
                      onClick={() => setSelectedProduct(product)}
                    >
                      {product.image_icon?.startsWith("http") ? (
                        <>
                          <img
                            src={getProductImageSrc(product.image_icon)}
                            alt={product.name}
                            loading="lazy"
                            decoding="async"
                            onError={handleProductImageError}
                          />
                          <span className="product-image-fallback" style={{ display: "none" }}>
                            📦
                          </span>
                        </>
                      ) : (
                        <span className="product-image-fallback">
                          {product.image_icon || "📦"}
                        </span>
                      )}
                    </button>

                    <div className="recommendation-info">
                      <span>{product.brand || "INTECTEC"}</span>
                      <h3>{product.name}</h3>
                      <p>{shortCategory(product.category)}</p>
                    </div>

                    <div className="recommendation-footer">
                      <strong>{money(product.price)}</strong>

                      <button
                        disabled={cartBusy || product.available <= 0}
                        onClick={() => addToCart(product)}
                      >
                        {product.available <= 0
                          ? "Sin inventario"
                          : cartBusy
                          ? "Agregando..."
                          : "Agregar"}
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        )}

        <section
          className="shop-section"
          id="catalogo"
        >
          <div className="section-title">
            <div>
              <span className="eyebrow">Catálogo</span>
              <h2>Productos disponibles</h2>
            </div>

            <p>
              {searchBusy
                ? "Buscando productos..."
                : `${totalCatalogProducts} productos`}
            </p>
          </div>

          <div className="shop-tools">
            <div className="search-box">
              <span>🔎</span>

              <input
                type="text"
                placeholder="Buscar..."
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>

            <select
              value={sort}
              onChange={(event) => setSort(event.target.value)}
            >
              <option value="featured">Destacados</option>
              <option value="price-low">Menor precio</option>
              <option value="price-high">Mayor precio</option>
              <option value="rating">Mejor rating</option>
            </select>
          </div>

          {remoteSearchActive && (
            <div className="search-status">
              Resultados de búsqueda para <strong>{search}</strong>
              {searchBusy && <span> · buscando...</span>}
            </div>
          )}

          {searchError && (
            <div className="catalog-error">
              {searchError}
            </div>
          )}

          <div className="categories">
            {categories.map((item) => (
              <button
                key={item}
                className={category === item ? "active" : ""}
                onClick={() => setCategory(item)}
              >
                {item}
              </button>
            ))}
          </div>

          {renderCatalogPagination("top")}

          {loadingProducts && (
            <div className="catalog-message">
              Cargando catálogo...
            </div>
          )}

          {errorProducts && (
            <div className="catalog-error">
              {errorProducts}
            </div>
          )}

          {!loadingProducts && (
            <div className="product-grid">
              {paginatedProducts.map((product) => (
                <article
                  className="product-card"
                  key={product.id}
                >
                  <div className="product-top">
                    <span className="badge">
                      {product.available > 0 ? product.badge || "Disponible" : "Sin inventario"}
                    </span>
                  </div>

                  <button
                    className="product-image"
                    onClick={() => setSelectedProduct(product)}
                  >
                    {product.image_icon?.startsWith("http") ? (
                      <>
                        <img
                          src={getProductImageSrc(product.image_icon)}
                          alt={product.name}
                          className="product-img"
                          loading="lazy"
                          decoding="async"
                          onError={handleProductImageError}
                        />
                        <span className="product-image-fallback" style={{ display: "none" }}>
                          📦
                        </span>
                      </>
                    ) : (
                      <span className="product-image-fallback">
                        {product.image_icon || "📦"}
                      </span>
                    )}
                  </button>

                  <div className="product-info">
                    <p className="product-category">
                      {shortCategory(product.category)}
                    </p>

                    <h3>{product.name}</h3>

                    <p className="brand">
                      {product.brand}
                    </p>

                    <div className="price-row">
                      <strong>{money(product.price)}</strong>
                    </div>

                    <p className="stock">
                      {availabilityLabel(product.available)}
                    </p>

                    <div className="product-actions">
                      <button
                        disabled={cartBusy || product.available <= 0}
                        onClick={() => addToCart(product)}
                      >
                        {product.available <= 0
                          ? "Sin inventario"
                          : cartBusy
                          ? "Procesando..."
                          : "Agregar"}
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        <section id="contacto" className="newsletter contact-section">
          <div className="contact-copy">
            <span className="eyebrow">Contacto</span>
            <h2>¿Necesitas una solución empresarial?</h2>
            <p>
              Cuéntanos qué necesitas y un asesor de Intectec se pondrá en contacto contigo.
            </p>
          </div>

          <form className="contact-form contact-form-full" onSubmit={handleContactSubmit}>
            <div className="contact-fields">
              <input
                type="text"
                placeholder="Nombre o empresa"
                value={contactName}
                onChange={(event) => {
                  setContactName(event.target.value);
                  setContactError("");
                  setContactStatus("");
                }}
                disabled={contactBusy}
              />

              <input
                type="email"
                placeholder="Correo electrónico"
                value={contactEmail}
                onChange={(event) => {
                  setContactEmail(event.target.value);
                  setContactError("");
                  setContactStatus("");
                }}
                disabled={contactBusy}
              />

              <input
                type="tel"
                placeholder="Teléfono"
                value={contactPhone}
                onChange={(event) => {
                  setContactPhone(event.target.value);
                  setContactError("");
                  setContactStatus("");
                }}
                disabled={contactBusy}
              />

              <textarea
                placeholder="Cuéntanos qué producto, servicio o solución necesitas"
                value={contactMessage}
                onChange={(event) => {
                  setContactMessage(event.target.value);
                  setContactError("");
                  setContactStatus("");
                }}
                disabled={contactBusy}
                rows="3"
              />
            </div>

            <button type="submit" disabled={contactBusy}>
              {contactBusy ? "Enviando..." : "Solicitar información"}
            </button>

            {(contactError || contactStatus) && (
              <div className={contactError ? "contact-message error" : "contact-message success"}>
                {contactError || contactStatus}
              </div>
            )}
          </form>
        </section>
      </main>

      <AccountPanels
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        lastOrder={lastOrder}
        loadingCart={loadingCart}
        cartItems={cartItems}
        cartBusy={cartBusy}
        updateQuantity={updateQuantity}
        removeItem={removeItem}
        totals={totals}
        selectedAddressId={selectedAddressId}
        setSelectedAddressId={setSelectedAddressId}
        addresses={addresses}
        openAddresses={openAddresses}
        selectedBillingId={selectedBillingId}
        setSelectedBillingId={setSelectedBillingId}
        billingProfiles={billingProfiles}
        openBilling={openBilling}
        checkoutBusy={checkoutBusy}
        checkout={checkout}
        clearCart={clearCart}
        ordersOpen={ordersOpen}
        setOrdersOpen={setOrdersOpen}
        loadingOrders={loadingOrders}
        orders={orders}
        cancelBusy={cancelBusy}
        cancelOrder={cancelOrder}
        addressesOpen={addressesOpen}
        setAddressesOpen={setAddressesOpen}
        loadingAddresses={loadingAddresses}
        addressForm={addressForm}
        setAddressForm={setAddressForm}
        saveAddress={saveAddress}
        addressBusy={addressBusy}
        billingOpen={billingOpen}
        setBillingOpen={setBillingOpen}
        loadingBilling={loadingBilling}
        billingForm={billingForm}
        setBillingForm={setBillingForm}
        saveBilling={saveBilling}
        billingBusy={billingBusy}
        selectedProduct={selectedProduct}
        setSelectedProduct={setSelectedProduct}
        addToCart={addToCart}
      />

      <AuthModals
        showLogin={showLogin}
        setShowLogin={setShowLogin}
        showRegister={showRegister}
        setShowRegister={setShowRegister}
        authError={authError}
        loginSubmit={loginSubmit}
        loginForm={loginForm}
        setLoginForm={setLoginForm}
        registerSubmit={registerSubmit}
        registerForm={registerForm}
        setRegisterForm={setRegisterForm}
        authBusy={authBusy}
      />

      {totalItems > 0 && (
        <button
          className="floating-cart"
          onClick={() => setDrawerOpen(true)}
        >
          🛒 {totalItems} producto(s) · {money(totals.total)}
        </button>
      )}
    </>
  );
}
